/**
 * <product-reviews-app> — renders for every visitor, signed in or not (the
 * section no longer gates on {% if customer %}: reading reviews doesn't
 * require an account). Only *writing* a review/reply needs a signed-in
 * customer, so buildLayout() swaps in a "Log in to write a review" prompt
 * for guests instead of the form. Talks to the review portal's public
 * endpoints:
 *   GET    /reviews/public?product_id=&customer_email=&page=&page_size=
 *   GET    /reviews/can-reply?customer_email= (whether this customer can reply — see checkCanReply)
 *   POST   /reviews
 *   POST   /reviews/upload-photos (multipart, multiple `files`; returns { photo_keys })
 *   POST   /reviews/{id}/public-reply, DELETE /reviews/{id}/public-reply
 *
 * Review content is user-submitted and displayed to other shoppers, so
 * anything derived from an API response is built with DOM APIs
 * (createElement/textContent/setAttribute) rather than innerHTML — that
 * keeps it safe by construction without hand-rolled escaping.
 *
 * Every API call carries this store's X-Store-Key (see storeKeyHeaders).
 * The key is required: the backend treats a missing key as Badais, so this
 * element renders a configuration error instead of calling the API without
 * one — otherwise a blank theme setting would show another store's reviews.
 *
 * When the section's "emit structured data" setting is on, it also injects
 * a Review JSON-LD block (see updateReviewJsonLd) built from that same
 * rendered data, linked by @id to the Product/ProductGroup that
 * sections/product-reviews.liquid's structured_data filter call emits (that
 * section separately emits its own aggregateRating fragment onto the same
 * @id — this file only ever adds the `review` array, never touches that).
 */

// Keep in sync with the backend's own limits (app/services/storage_service.py)
// — this is just an early client-side check, the backend re-validates regardless.
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_PHOTOS_PER_REVIEW = 5;

/** @param {string} tag @param {Record<string, string>} [attrs] @param {(Node|string)[]} [children] */
function h(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value == null) continue;
    if (key === 'class') node.className = value;
    else node.setAttribute(key, value);
  }
  for (const child of children) {
    if (child == null) continue;
    node.append(child);
  }
  return node;
}

/** "Adjustments" icon for the rating filter control — built with the SVG
 * namespace directly since h() only creates HTML elements. */
function buildFilterIcon() {
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  for (const [x1, y1, x2, y2] of [
    [2, 4, 14, 4],
    [2, 8, 14, 8],
    [2, 12, 14, 12],
  ]) {
    const line = document.createElementNS(svgNS, 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', 'currentColor');
    line.setAttribute('stroke-width', '1.2');
    svg.append(line);
  }
  for (const [cx, cy] of [
    [6, 4],
    [10, 8],
    [5, 12],
  ]) {
    const circle = document.createElementNS(svgNS, 'circle');
    circle.setAttribute('cx', String(cx));
    circle.setAttribute('cy', String(cy));
    circle.setAttribute('r', '1.6');
    circle.setAttribute('fill', 'currentColor');
    svg.append(circle);
  }
  return svg;
}

// Same rounded 5-point star path as the "Overall Rating" stars rendered in
// Liquid (sections/product-reviews.liquid)
// — kept identical so the review list's stars match, since a filled SVG
// path's corners can't be rounded via CSS the way a stroke's can.
const ROUNDED_STAR_PATH_D =
  'M6.435 1.952 Q7.000 0.000 7.565 1.952 L7.781 2.695 Q8.346 4.647 10.377 4.582 L11.151 4.557 Q13.182 4.491 11.500 5.632 ' +
  'L10.860 6.067 Q9.178 7.208 9.868 9.119 L10.131 9.847 Q10.821 11.759 9.216 10.512 L8.605 10.037 Q7.000 8.790 5.395 10.037 ' +
  'L4.784 10.512 Q3.179 11.759 3.869 9.847 L4.132 9.119 Q4.822 7.208 3.140 6.067 L2.500 5.632 Q0.818 4.491 2.849 4.557 ' +
  'L3.623 4.582 Q5.654 4.647 6.219 2.695 Z';

/** One rating star, filled or not — used for the review-card star rows. */
function buildStarIcon(filled) {
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '13');
  svg.setAttribute('viewBox', '0 0 14 13');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svgNS, 'path');
  path.setAttribute('d', ROUNDED_STAR_PATH_D);
  path.setAttribute('fill', filled ? '#F5A623' : '#D9D9D9');
  svg.append(path);
  return svg;
}

/** Small picture-frame icon for the photo picker button. */
function buildImageIcon() {
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  const rect = document.createElementNS(svgNS, 'rect');
  rect.setAttribute('x', '1.5');
  rect.setAttribute('y', '2.5');
  rect.setAttribute('width', '13');
  rect.setAttribute('height', '11');
  rect.setAttribute('rx', '1.5');
  rect.setAttribute('stroke', 'currentColor');
  rect.setAttribute('stroke-width', '1.2');
  svg.append(rect);
  const sun = document.createElementNS(svgNS, 'circle');
  sun.setAttribute('cx', '5.2');
  sun.setAttribute('cy', '6');
  sun.setAttribute('r', '1.1');
  sun.setAttribute('fill', 'currentColor');
  svg.append(sun);
  const mountains = document.createElementNS(svgNS, 'path');
  mountains.setAttribute('d', 'M2 11.5L5.5 8L8 10L11 7L14 10.5');
  mountains.setAttribute('stroke', 'currentColor');
  mountains.setAttribute('stroke-width', '1.2');
  mountains.setAttribute('fill', 'none');
  svg.append(mountains);
  return svg;
}

/** Chevron for the pagination buttons. direction: 'left' | 'right'. */
function buildChevronIcon(direction) {
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svgNS, 'path');
  path.setAttribute('d', direction === 'left' ? 'M10 3L5 8l5 5' : 'M6 3l5 5-5 5');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.5');
  svg.append(path);
  return svg;
}

class ProductReviewsApp extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    // Root-relative product path (e.g. "/products/handle"), used only to
    // build the "<url>#product" @id for updateReviewJsonLd — must match
    // sections/product-reviews.liquid's own construction exactly.
    this.productUrl = this.dataset.productUrl || '';
    this.customerEmail = this.dataset.customerEmail || '';
    // Always sourced from the Shopify customer object (see product-reviews.liquid)
    // — no manual name field in the form. Can be empty if Shopify has no
    // name on file (e.g. a passwordless signup that never set one); in that
    // case buildLayout() shows a prompt to add one instead of the form.
    this.customerName = this.dataset.customerName || '';
    // Resolved async (see checkCanReply) — false until that check
    // completes, so the reply/edit/remove controls never flash on and then
    // have to disappear.
    this.customerCanReply = false;
    this.accountUrl = this.dataset.accountUrl || '';
    this.loginUrl = this.dataset.loginUrl || '';
    // The CTA cards' background artwork — a designer-supplied asset, not
    // hand-drawn — resolved by Liquid via asset_url since a plain .css
    // file can't call that filter itself. See buildReviewCta.
    this.decorationUrl = this.dataset.decorationUrl || '';
    this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
    // Identifies which store this is to the backend — see storeKeyHeaders().
    this.storeKey = this.dataset.storeKey || '';
    // Shown on store replies ("Reply from <name>") — a section setting, so
    // no store name is hardcoded here.
    this.storeName = this.dataset.storeName || '';
    // Whether to inject Review JSON-LD at all — mirrors the section's
    // emit_structured_data setting, since the Review array only makes sense
    // when that section also emits the Product entity it links to by @id.
    this.reviewJsonLdEnabled = this.dataset.reviewJsonld === 'true';
    this.pageSize = Number(this.dataset.pageSize) || 5;
    this.page = 1;
    // Both default to the same "no filter, most-neutral sort" view the very
    // first loadReviews(1) call below fetches — see updateReviewJsonLd,
    // which only ever runs once, against that first response.
    this.ratingFilter = null;
    this.sort = 'newest';
    this.jsonLdInjected = false;

    // Without a key the backend would answer as Badais — never call it.
    if (!this.storeKey) {
      console.error('product-reviews: no store key configured (section setting "Reviews store key").');
      this.replaceChildren(h('p', { class: 'product-reviews-app__error' }, ['Reviews are unavailable right now.']));
      return;
    }

    this.buildLayout();
    this.loadReviews(1);
    this.loadDistribution();
    this.checkCanReply();
  }

  /** Spread into every fetch's headers. connectedCallback guarantees
   * storeKey is non-empty before any fetch can run. */
  storeKeyHeaders() {
    return { 'X-Store-Key': this.storeKey };
  }

  /**
   * Whether this signed-in customer can actually reply/edit/remove a reply
   * — resolved from the backend (GET /reviews/can-reply), not guessed from
   * email domain. Reply permission is a real per-account grant now (see the
   * admin dashboard's User Management page), not "anyone at this email
   * domain", so this can't be decided client-side. Runs alongside the
   * first loadReviews() rather than blocking on it; if it comes back true,
   * reloads the already-visible list once so the reply controls appear
   * without the customer needing to do anything (e.g. change page).
   */
  async checkCanReply() {
    if (!this.customerEmail) return;
    try {
      const url = new URL(`${this.apiBase}/reviews/can-reply`);
      url.searchParams.set('customer_email', this.customerEmail);
      const response = await fetch(url.toString(), { headers: this.storeKeyHeaders() });
      if (!response.ok) return;
      const body = await response.json();
      if (body.can_reply) {
        this.customerCanReply = true;
        this.loadReviews(this.page);
      }
    } catch {
      // Leave customerCanReply false — same outcome as a permission denial,
      // the reply UI just doesn't show. Reading reviews (and any reply
      // already on one) is unaffected either way.
    }
  }

  /**
   * Figma puts the rating snapshot, the (Liquid-rendered) overall rating,
   * and this element's own write-a-review CTA in one shared panel — see
   * sections/product-reviews.liquid's .product-reviews-panel. Since the
   * overall-rating column has to stay server-rendered in Liquid (the
   * average_rating/review_count values, not a JS fetch), this element
   * mounts its own two pieces into the two slot elements Liquid leaves for
   * them (data-snapshot-target/data-cta-target) rather than appending them
   * to itself, so the DOM order across both sources matches the design.
   */
  buildLayout() {
    this.replaceChildren();

    this.snapshotTarget = document.getElementById(this.dataset.snapshotTarget);
    this.ctaTarget = document.getElementById(this.dataset.ctaTarget);
    this.snapshotTarget?.replaceChildren(this.buildRatingSnapshot());
    this.ctaTarget?.replaceChildren(this.buildCtaOrPrompt());

    // -- Review list + pagination ----------------------------------------
    this.listEl = h('div', { class: 'product-reviews-app__list', 'aria-live': 'polite' });

    // Range text on the left, chevron-only prev/next on the right — the
    // buttons carry aria-labels since they have no visible text.
    this.prevButton = h('button', { type: 'button', class: 'product-reviews-app__page-button', 'aria-label': 'Previous page' }, [
      buildChevronIcon('left'),
    ]);
    this.nextButton = h('button', { type: 'button', class: 'product-reviews-app__page-button', 'aria-label': 'Next page' }, [
      buildChevronIcon('right'),
    ]);
    this.pageLabelEl = h('p', { class: 'product-reviews-app__page-label' });
    this.prevButton.addEventListener('click', () => this.goToPage(this.page - 1));
    this.nextButton.addEventListener('click', () => this.goToPage(this.page + 1));
    this.paginationEl = h('div', { class: 'product-reviews-app__pagination', hidden: 'hidden' }, [
      this.pageLabelEl,
      h('div', { class: 'product-reviews-app__page-buttons' }, [this.prevButton, this.nextButton]),
    ]);

    this.append(this.buildFilterBar(), this.listEl, this.paginationEl, this.buildLightbox(), this.buildConfirmDialog());
  }

  buildCtaOrPrompt() {
    if (!this.customerEmail) return this.buildLoginPrompt();
    if (!this.customerName) return this.buildNamePrompt();
    return this.buildReviewCta();
  }

  /**
   * "Rating Snapshot" bars (5★-1★ with counts) — fetched once from
   * GET /reviews/public/distribution and left alone after that: unlike the
   * review list, this represents the whole approved review set, not
   * whatever rating filter is currently applied to the list below it.
   * Hidden until loadDistribution() has real counts to show.
   */
  buildRatingSnapshot() {
    this.ratingSnapshotRowsEl = h('div', { class: 'product-reviews-app__rating-snapshot-rows' });
    this.ratingSnapshotEl = h('div', { class: 'product-reviews-app__rating-snapshot', hidden: 'hidden' }, [
      h('h3', {}, ['Rating Snapshot']),
      this.ratingSnapshotRowsEl,
    ]);
    return this.ratingSnapshotEl;
  }

  async loadDistribution() {
    try {
      const url = new URL(`${this.apiBase}/reviews/public/distribution`);
      url.searchParams.set('product_id', this.productId);
      const response = await fetch(url.toString(), { headers: this.storeKeyHeaders() });
      if (!response.ok) return;
      this.renderRatingSnapshot(await response.json());
    } catch {
      // Leave the snapshot hidden — same fail-soft posture as checkCanReply.
    }
  }

  renderRatingSnapshot(data) {
    if (!data.review_count) return; // nothing approved yet — matches the "No reviews yet" summary above
    this.ratingSnapshotRowsEl.replaceChildren(
      ...data.counts.map((bucket) => {
        const pct = Math.round((bucket.count / data.review_count) * 100);
        return h('div', { class: 'product-reviews-app__rating-snapshot-row' }, [
          h('span', { class: 'product-reviews-app__rating-snapshot-label' }, [`${bucket.rating} star${bucket.rating > 1 ? 's' : ''}`]),
          h('div', { class: 'product-reviews-app__rating-snapshot-bar' }, [
            h('div', { class: 'product-reviews-app__rating-snapshot-bar-fill', style: `width: ${pct}%` }),
          ]),
          h('span', { class: 'product-reviews-app__rating-snapshot-count' }, [String(bucket.count)]),
        ]);
      }),
    );
    this.ratingSnapshotEl.hidden = false;
  }

  /**
   * Filter-by-rating + sort-by controls for the review list, plus the
   * "X–Y of N Reviews" range text between them (updated on every
   * loadReviews response — see there). Changing either resets to page 1
   * and re-fetches — neither ever touches the rating snapshot above
   * (that's the whole approved set, always) or re-runs the JSON-LD
   * injector (see loadReviews/updateReviewJsonLd), since only the true
   * first load's default view is ever indexed.
   *
   * The rating filter's visible label always reads "Rating" regardless of
   * the active selection (matching the design) — a real <select> sits
   * absolutely positioned over the label, invisible but still the thing
   * that actually receives the click/tap and stays keyboard/screen-reader
   * accessible, rather than a fully custom (and non-native) dropdown.
   */
  buildFilterBar() {
    this.ratingFilterSelect = h(
      'select',
      { id: 'review-rating-filter', class: 'product-reviews-app__rating-filter-select', 'aria-label': 'Filter by rating' },
      [
        h('option', { value: '' }, ['All ratings']),
        ...[5, 4, 3, 2, 1].map((n) => h('option', { value: String(n) }, [`${n} star${n > 1 ? 's' : ''}`])),
      ],
    );
    this.ratingFilterSelect.addEventListener('change', () => {
      this.ratingFilter = this.ratingFilterSelect.value || null;
      this.loadReviews(1);
    });
    const ratingFilterLabel = h('label', { for: 'review-rating-filter', class: 'product-reviews-app__rating-filter' }, [
      buildFilterIcon(),
      h('span', {}, ['Rating']),
      this.ratingFilterSelect,
    ]);

    this.sortSelect = h('select', { id: 'review-sort', class: 'product-reviews-app__sort-select', 'aria-label': 'Sort reviews by' }, [
      h('option', { value: 'newest' }, ['Newest']),
      h('option', { value: 'relevant' }, ['Most Relevant']),
      h('option', { value: 'highest_rated' }, ['Highest Rated']),
    ]);
    this.sortSelect.value = this.sort;
    this.sortSelect.addEventListener('change', () => {
      this.sort = this.sortSelect.value;
      this.loadReviews(1);
    });
    const sortLabel = h('label', { for: 'review-sort', class: 'product-reviews-app__sort-field' }, [
      h('span', {}, ['Sort by:']),
      this.sortSelect,
    ]);

    this.reviewRangeEl = h('p', { class: 'product-reviews-app__review-range', 'aria-live': 'polite' });

    return h('div', { class: 'product-reviews-app__filter-section' }, [
      h('p', { class: 'product-reviews-app__filter-heading' }, ['Filter Reviews']),
      h('div', { class: 'product-reviews-app__filter-bar' }, [ratingFilterLabel, this.reviewRangeEl, sortLabel]),
    ]);
  }

  /**
   * One shared <dialog> for confirming a destructive action (currently just
   * "Remove reply") — same native <dialog> approach as buildLightbox() and
   * for the same reasons (focus trap, Esc-to-cancel, ::backdrop, all free).
   * confirmAction() shows it and returns a Promise<boolean> resolved by
   * whichever button (or Esc/backdrop) closed it.
   */
  buildConfirmDialog() {
    this.confirmMessageEl = h('p', { class: 'product-reviews-app__confirm-message' });
    const cancelButton = h('button', { type: 'button', class: 'product-reviews-app__button product-reviews-app__button--secondary' }, ['Cancel']);
    this.confirmActionButton = h(
      'button',
      { type: 'button', class: 'product-reviews-app__button product-reviews-app__confirm-danger-button' },
      ['Remove reply'],
    );

    const resolveWith = (value) => {
      const resolve = this.confirmResolve;
      this.confirmResolve = null;
      if (this.confirmDialogEl.open) this.confirmDialogEl.close();
      resolve?.(value);
    };

    cancelButton.addEventListener('click', () => resolveWith(false));
    this.confirmActionButton.addEventListener('click', () => resolveWith(true));

    this.confirmDialogEl = h('dialog', { class: 'product-reviews-app__confirm-dialog' }, [
      this.confirmMessageEl,
      h('div', { class: 'product-reviews-app__confirm-actions' }, [cancelButton, this.confirmActionButton]),
    ]);
    // Esc fires the dialog's native "cancel" event before it closes itself
    // — treat that the same as clicking Cancel. Backdrop click doesn't
    // close a <dialog> on its own, so it's handled manually, same as the
    // lightbox.
    this.confirmDialogEl.addEventListener('cancel', () => resolveWith(false));
    this.confirmDialogEl.addEventListener('click', (event) => {
      if (event.target === this.confirmDialogEl) resolveWith(false);
    });
    return this.confirmDialogEl;
  }

  /** message: shown in the dialog. actionLabel: text for the confirm
   * button (defaults to "Remove reply", the only destructive action today).
   * Resolves true if confirmed, false if cancelled/dismissed. */
  confirmAction(message, actionLabel = 'Remove reply') {
    this.confirmMessageEl.textContent = message;
    this.confirmActionButton.textContent = actionLabel;
    this.confirmDialogEl.showModal();
    return new Promise((resolve) => {
      this.confirmResolve = resolve;
    });
  }

  /**
   * One shared <dialog> for viewing review photos full-size, reused for
   * whichever review's gallery was clicked. Native <dialog>.showModal()
   * gives us a real modal — focus trapping, ::backdrop, and Esc-to-close —
   * for free; backdrop-click-to-close and prev/next navigation are the only
   * bits handled manually.
   */
  buildLightbox() {
    this.lightboxImages = [];
    this.lightboxIndex = 0;

    this.lightboxImage = h('img', { alt: 'Review photo, full size' });
    this.lightboxCloseButton = h(
      'button',
      { type: 'button', class: 'product-reviews-app__lightbox-close', 'aria-label': 'Close' },
      ['×'],
    );
    this.lightboxPrevButton = h(
      'button',
      { type: 'button', class: 'product-reviews-app__lightbox-nav product-reviews-app__lightbox-nav--prev', 'aria-label': 'Previous photo' },
      ['‹'],
    );
    this.lightboxNextButton = h(
      'button',
      { type: 'button', class: 'product-reviews-app__lightbox-nav product-reviews-app__lightbox-nav--next', 'aria-label': 'Next photo' },
      ['›'],
    );
    this.lightboxCounter = h('p', { class: 'product-reviews-app__lightbox-counter' });

    this.lightboxCloseButton.addEventListener('click', () => this.lightboxEl.close());
    this.lightboxPrevButton.addEventListener('click', () => this.showLightboxImage(this.lightboxIndex - 1));
    this.lightboxNextButton.addEventListener('click', () => this.showLightboxImage(this.lightboxIndex + 1));

    this.lightboxEl = h('dialog', { class: 'product-reviews-app__lightbox' }, [
      this.lightboxCloseButton,
      this.lightboxPrevButton,
      this.lightboxImage,
      this.lightboxNextButton,
      this.lightboxCounter,
    ]);
    this.lightboxEl.addEventListener('click', (event) => {
      if (event.target === this.lightboxEl) this.lightboxEl.close(); // backdrop, not the image/buttons
    });
    this.lightboxEl.addEventListener('keydown', (event) => {
      if (this.lightboxImages.length <= 1) return;
      if (event.key === 'ArrowLeft') this.showLightboxImage(this.lightboxIndex - 1);
      if (event.key === 'ArrowRight') this.showLightboxImage(this.lightboxIndex + 1);
    });
    return this.lightboxEl;
  }

  showLightboxImage(index) {
    const count = this.lightboxImages.length;
    this.lightboxIndex = (index + count) % count;
    this.lightboxImage.src = this.lightboxImages[this.lightboxIndex];
    this.lightboxCounter.textContent = `${this.lightboxIndex + 1} / ${count}`;
  }

  /** photoUrls: string[]; startIndex: which one was clicked. */
  openLightbox(photoUrls, startIndex) {
    this.lightboxImages = photoUrls;
    const hasMultiple = photoUrls.length > 1;
    this.lightboxPrevButton.hidden = !hasMultiple;
    this.lightboxNextButton.hidden = !hasMultiple;
    this.lightboxCounter.hidden = !hasMultiple;
    this.showLightboxImage(startIndex);
    this.lightboxEl.showModal();
  }

  /**
   * The write-a-review CTA has three mutually-exclusive states, matching
   * the design: a compact "Rate" prompt (brand red, just a heading + star
   * row) → the full "Review" form (red, shown once a star is picked) →
   * "Success" (red, shown after a successful submit — see
   * showSubmittedPanel). Only one of ctaRateEl/formEl/submittedPanelEl is
   * visible at a time; showCtaState() toggles between them.
   */
  buildReviewCta() {
    // Built 5→1 (reverse of display order) on purpose: paired with
    // flex-direction: row-reverse in CSS, this keeps 1★ leftmost visually
    // while putting it LAST in the DOM — which is what lets the plain CSS
    // sibling selector `:hover ~ .product-reviews-cta__star` fill every
    // star up to and including the hovered one, without any JS.
    this.ctaStarButtons = [5, 4, 3, 2, 1].map((n) =>
      h(
        'button',
        { type: 'button', class: 'product-reviews-cta__star', 'data-value': String(n), 'aria-label': `${n} star${n > 1 ? 's' : ''}` },
        ['★'],
      ),
    );
    this.ctaStarButtons.forEach((btn) =>
      btn.addEventListener('click', () => {
        this.setRating(Number(btn.dataset.value));
        this.showCtaState('form');
      }),
    );
    this.ctaRateEl = h('div', { class: 'product-reviews-cta__rate' }, [
      h('h3', {}, ['Review this Product']),
      h('div', { class: 'product-reviews-cta__rate-stars', role: 'radiogroup', 'aria-label': 'Rating' }, this.ctaStarButtons),
      h('p', { class: 'product-reviews-cta__hint' }, ['Adding a review will require a valid email for verification']),
    ]);

    this.buildReviewForm();

    this.submittedMessageEl = h('p', { class: 'product-reviews-app__submitted-message' });
    this.submittedPanelEl = h('div', { class: 'product-reviews-app__submitted-panel', hidden: 'hidden' }, [
      h('div', { class: 'product-reviews-app__submitted-check', 'aria-hidden': 'true' }, ['✓']),
      h('p', { class: 'product-reviews-app__submitted-title' }, ['Review Submitted']),
      this.submittedMessageEl,
      h('a', { class: 'product-reviews-app__button', href: '/' }, ['Continue Shopping']),
    ]);

    // Designer-supplied rose artwork, a transparent PNG set as a corner
    // background on the "Rate" and "Success" cards (not the form, where it
    // would sit behind the inputs), per the design. The URL comes from
    // Liquid (see product-reviews.liquid), since a plain .css asset can't
    // call the asset_url filter itself.
    if (this.decorationUrl) {
      this.ctaRateEl.style.backgroundImage = `url(${this.decorationUrl})`;
      this.submittedPanelEl.style.backgroundImage = `url(${this.decorationUrl})`;
    }

    this.formEl.hidden = true; // Rate state shown first — see showCtaState('rate') below.
    this.ctaEl = h('div', { class: 'product-reviews-cta' }, [this.ctaRateEl, this.formEl, this.submittedPanelEl]);
    this.showCtaState('rate');
    return this.ctaEl;
  }

  showCtaState(state) {
    this.ctaRateEl.hidden = state !== 'rate';
    this.formEl.hidden = state !== 'form';
    this.submittedPanelEl.hidden = state !== 'submitted';
  }

  /**
   * Builds the full review form (title/text/photos/submit) into this.formEl.
   * No star row here — the rating was already captured by ctaStarButtons in
   * the "Rate" state (see buildReviewCta) and carries over silently via
   * this.ratingInput, matching the design (the "Review" step never shows or
   * re-asks for a rating).
   */
  buildReviewForm() {
    this.ratingInput = h('input', { type: 'hidden', name: 'rating' });
    this.titleInput = h('input', {
      type: 'text',
      name: 'review_title',
      maxlength: '255',
      id: 'review-title',
      placeholder: 'Review Title',
    });
    // Optional — a rating (+ maybe just a photo) is a valid review on its own.
    this.textInput = h('textarea', { name: 'review_text', id: 'review-text', rows: '4', placeholder: 'Your Review' });
    this.errorEl = h('p', { class: 'product-reviews-app__form-message product-reviews-app__form-message--error', hidden: 'hidden' });
    this.submitButton = h('button', { type: 'submit', class: 'product-reviews-app__button' }, ['Submit Review']);

    // -- Photos (optional, up to MAX_PHOTOS_PER_REVIEW) ---------------------
    // The native <input type="file"> ("Choose File / No file chosen") can't
    // be restyled directly in any browser, so it's visually hidden
    // (.product-reviews-app__photo-input, still focusable/functional) and a
    // real <label for="review-photos"> styled as a button is the visible
    // trigger — clicking a label activates its associated input natively,
    // no JS needed for that part. Re-opening the picker with `multiple`
    // *replaces* the previous selection in the browser, so selections are
    // accumulated here in `selectedPhotoFiles` across picker uses instead of
    // trusting the input's own .files each time.
    this.selectedPhotoFiles = [];
    this.photoInput = h('input', {
      type: 'file',
      name: 'photos',
      id: 'review-photos',
      class: 'product-reviews-app__photo-input',
      accept: ALLOWED_PHOTO_TYPES.join(','),
      multiple: 'multiple',
    });
    this.photoPickerLabel = h('label', { for: 'review-photos', class: 'product-reviews-app__photo-picker-label' }, [
      buildImageIcon(),
      `Choose up to ${MAX_PHOTOS_PER_REVIEW} photos`,
    ]);
    // Empty until photos are actually selected (see renderPhotoPreviews) —
    // the button's own label already says "up to N photos", so there's
    // nothing to show until there's a count/limit worth reporting.
    this.photoHintEl = h('span', { class: 'product-reviews-app__photo-hint' });
    this.photoPreviewGrid = h('div', { class: 'product-reviews-app__photo-preview-grid' });
    this.photoInput.addEventListener('change', () => this.handlePhotosSelected());

    this.formEl = h('form', { class: 'product-reviews-app__form', novalidate: 'novalidate' }, [
      h('h3', {}, ['Write a Review']),
      this.ratingInput,
      h('label', { for: 'review-title' }, [
        h('span', { class: 'product-reviews-app__visually-hidden' }, ['Review title']),
        this.titleInput,
      ]),
      h('label', { for: 'review-text' }, [
        h('span', { class: 'product-reviews-app__visually-hidden' }, ['Your review']),
        this.textInput,
      ]),
      h('div', { class: 'product-reviews-app__photo-field' }, [
        h('div', { class: 'product-reviews-app__photo-picker' }, [this.photoInput, this.photoPickerLabel, this.photoHintEl]),
        this.photoPreviewGrid,
      ]),
      this.errorEl,
      this.submitButton,
    ]);
    this.formEl.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  showSubmittedPanel(message) {
    this.submittedMessageEl.textContent = message;
    this.showCtaState('submitted');
  }

  handlePhotosSelected() {
    this.hideMessages();
    const newFiles = Array.from(this.photoInput.files || []);
    this.photoInput.value = ''; // so picking the same file(s) again still fires `change`

    for (const file of newFiles) {
      if (this.selectedPhotoFiles.length >= MAX_PHOTOS_PER_REVIEW) {
        this.showError(`You can attach up to ${MAX_PHOTOS_PER_REVIEW} photos.`);
        break;
      }
      if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
        this.showError('Please choose JPEG, PNG, WEBP, or GIF images.');
        continue;
      }
      if (file.size > MAX_PHOTO_SIZE_BYTES) {
        this.showError(`"${file.name}" is too large — max ${MAX_PHOTO_SIZE_BYTES / (1024 * 1024)}MB.`);
        continue;
      }
      this.selectedPhotoFiles.push(file);
    }
    this.renderPhotoPreviews();
  }

  removeSelectedPhoto(index) {
    const [removed] = this.selectedPhotoFiles.splice(index, 1);
    if (removed?.objectUrl) URL.revokeObjectURL(removed.objectUrl);
    this.renderPhotoPreviews();
  }

  renderPhotoPreviews() {
    this.photoPreviewGrid.replaceChildren(
      ...this.selectedPhotoFiles.map((file, index) => {
        const objectUrl = URL.createObjectURL(file);
        const removeButton = h(
          'button',
          { type: 'button', class: 'product-reviews-app__photo-preview-remove', 'aria-label': `Remove photo ${index + 1}` },
          ['×'],
        );
        removeButton.addEventListener('click', () => this.removeSelectedPhoto(index));
        return h('div', { class: 'product-reviews-app__photo-preview-item' }, [
          h('img', { src: objectUrl, alt: '' }),
          removeButton,
        ]);
      }),
    );
    const atLimit = this.selectedPhotoFiles.length >= MAX_PHOTOS_PER_REVIEW;
    this.photoPickerLabel.setAttribute('aria-disabled', String(atLimit));
    this.photoInput.disabled = atLimit;
    this.photoHintEl.textContent = atLimit
      ? `Maximum of ${MAX_PHOTOS_PER_REVIEW} photos reached`
      : `${this.selectedPhotoFiles.length}/${MAX_PHOTOS_PER_REVIEW} photos`;
  }

  clearSelectedPhotos() {
    this.selectedPhotoFiles = [];
    this.photoInput.value = '';
    this.photoInput.disabled = false;
    this.photoPickerLabel.setAttribute('aria-disabled', 'false');
    this.photoHintEl.textContent = `Up to ${MAX_PHOTOS_PER_REVIEW} photos`;
    this.photoPreviewGrid.replaceChildren();
  }

  /** Uploads all selected photos (if any) and returns their storage keys. */
  async uploadSelectedPhotos() {
    if (!this.selectedPhotoFiles.length) return [];

    const formData = new FormData();
    for (const file of this.selectedPhotoFiles) formData.append('files', file);

    const response = await fetch(`${this.apiBase}/reviews/upload-photos`, {
      method: 'POST',
      headers: this.storeKeyHeaders(),
      body: formData,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(typeof body.detail === 'string' ? body.detail : 'Could not upload your photos. Please try again.');
    }
    const data = await response.json();
    return data.photo_keys;
  }

  /** Shown instead of the form for a guest (not signed in). Reading reviews still works. */
  buildLoginPrompt() {
    const link = h('a', { class: 'product-reviews-app__button', href: this.loginUrl || '#' }, ['Log in']);
    return h('div', { class: 'product-reviews-app__name-prompt' }, [
      h('p', {}, ['Log in to write a review.']),
      link,
    ]);
  }

  /** Shown instead of the form when Shopify has no name on file for this customer. */
  buildNamePrompt() {
    const link = h('a', { class: 'product-reviews-app__button', href: this.accountUrl || '#' }, ['Update my account']);
    return h('div', { class: 'product-reviews-app__name-prompt' }, [
      h('p', {}, ['Please add your name to your account before writing a review.']),
      link,
    ]);
  }

  setRating(value) {
    this.ratingInput.value = String(value);
    this.ctaStarButtons.forEach((btn) => btn.classList.toggle('is-filled', Number(btn.dataset.value) <= value));
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.hideMessages();

    const rating = Number(this.ratingInput.value);
    const reviewText = this.textInput.value.trim();
    const reviewTitle = this.titleInput.value.trim();

    if (!rating) {
      this.showError('Please select a star rating.');
      return;
    }

    this.submitButton.disabled = true;
    try {
      const photoKeys = await this.uploadSelectedPhotos();

      const response = await fetch(`${this.apiBase}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.storeKeyHeaders() },
        body: JSON.stringify({
          product_id: this.productId,
          customer_name: this.customerName,
          customer_email: this.customerEmail,
          rating,
          review_text: reviewText,
          review_title: reviewTitle || null,
          photo_keys: photoKeys,
        }),
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Could not submit your review. Please try again.');
      }

      this.formEl.reset();
      this.setRating(0);
      this.clearSelectedPhotos();
      // High-rated reviews publish immediately (see the backend's
      // auto-approval rule); others go to the moderation queue first.
      this.showSubmittedPanel(
        body.status === 'approved'
          ? 'Thanks! Your review has been published.'
          : 'Thanks! Your review has been submitted and is pending approval.',
      );
      this.loadReviews(1);
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    } finally {
      this.submitButton.disabled = false;
    }
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideMessages() {
    this.errorEl.hidden = true;
  }

  goToPage(page) {
    if (page < 1) return;
    if (this.totalPages && page > this.totalPages) return;
    this.loadReviews(page);
  }

  async loadReviews(page) {
    this.listEl.replaceChildren(h('p', { class: 'product-reviews-app__loading' }, ['Loading reviews…']));
    this.paginationEl.hidden = true;

    const url = new URL(`${this.apiBase}/reviews/public`);
    url.searchParams.set('product_id', this.productId);
    if (this.customerEmail) url.searchParams.set('customer_email', this.customerEmail);
    if (this.ratingFilter) url.searchParams.set('rating', this.ratingFilter);
    url.searchParams.set('sort', this.sort);
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(this.pageSize));

    try {
      const response = await fetch(url.toString(), { headers: this.storeKeyHeaders() });
      if (!response.ok) throw new Error('Could not load reviews.');
      const data = await response.json();

      this.page = data.page;
      this.totalPages = data.total_pages;
      this.renderList(data.items);
      this.renderPagination(data.page, data.total_pages);
      const rangeText = data.total
        ? `${(data.page - 1) * data.page_size + 1} – ${Math.min(data.page * data.page_size, data.total)} of ${data.total} Review${data.total === 1 ? '' : 's'}`
        : '';
      this.reviewRangeEl.textContent = rangeText;
      this.pageLabelEl.textContent = rangeText;
      // Runs exactly once, ever, against the very first response — which is
      // always the true default view (page 1, no rating filter, default
      // "newest" sort), since that's what connectedCallback's initial
      // loadReviews(1) call requests before any filter/sort UI can change
      // it. A crawler's rendering pass only ever observes that first load
      // (it never clicks Prev/Next or touches the filter/sort controls), so
      // deliberately never re-running this keeps the indexed Review array
      // matching what was actually indexed, rather than drifting to
      // whatever filter/sort a real visitor happens to leave it on.
      if (!this.jsonLdInjected && this.reviewJsonLdEnabled) {
        this.updateReviewJsonLd(data.items);
        this.jsonLdInjected = true;
      }
    } catch {
      this.listEl.replaceChildren(h('p', { class: 'product-reviews-app__error' }, ['Could not load reviews. Please try again later.']));
    }
  }

  /**
   * Injects/replaces a <script type="application/ld+json"> carrying a
   * `review` array, linked by @id to the same Product/ProductGroup entity
   * that sections/product-reviews.liquid's aggregateRating fragment merges onto
   * (see that file for why a shared @id makes Google treat these as one
   * entity instead of two). Built from the exact `items` just rendered by
   * renderList() — never from a separate fetch or a broader set — so the
   * structured data can never claim a review exists that isn't visibly on
   * the page right now.
   *
   * Only approved reviews with actual body text are eligible: a signed-in
   * customer's own pending/rejected review can appear in their personal
   * rendered list (see /reviews/public), but it must never appear in
   * structured data — status is already present on every item for exactly
   * this check, even though renderReview() never displays it (see there).
   *
   * Called exactly once per page view, from loadReviews' first response
   * only (see there) — not re-run after a filter/sort change or even after
   * this visitor's own review submission reloads the list, since none of
   * that is what a crawler's own single rendering pass would ever see.
   * Still keyed by a stable id (rather than just appending) purely so a
   * hot-reloaded/re-initialized element can't leave a stale duplicate
   * behind from a previous mount.
   */
  updateReviewJsonLd(items) {
    const existing = document.getElementById('product-reviews-jsonld');
    const eligible = items.filter((review) => review.status === 'approved' && review.review_text);

    if (!this.productUrl || !eligible.length) {
      existing?.remove();
      return;
    }

    const jsonLd = {
      '@context': 'https://schema.org',
      '@id': `${this.productUrl}#product`,
      review: eligible.map((review) => {
        const reviewObject = {
          '@type': 'Review',
          author: { '@type': 'Person', name: review.display_name },
          datePublished: new Date(review.created_at).toISOString().slice(0, 10),
          reviewBody: review.review_text,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: String(review.rating),
            bestRating: '5',
            worstRating: '1',
          },
        };
        if (review.review_title) reviewObject.name = review.review_title;
        return reviewObject;
      }),
    };

    const scriptEl = existing || document.createElement('script');
    scriptEl.type = 'application/ld+json';
    scriptEl.id = 'product-reviews-jsonld';
    // textContent, not innerHTML — same posture as the rest of this file.
    // Safe here for an extra reason too: unlike a server-rendered Liquid
    // string, a literal "</script" inside review text can't break out of
    // this tag, since textContent is never HTML-parsed.
    scriptEl.textContent = JSON.stringify(jsonLd);
    if (!existing) document.head.appendChild(scriptEl);
  }

  renderList(items) {
    if (!items.length) {
      this.listEl.replaceChildren(h('p', { class: 'product-reviews-app__empty' }, ['No reviews yet. Be the first to write one!']));
      return;
    }
    this.listEl.replaceChildren(...items.map((review) => this.renderReview(review)));
  }

  /**
   * Reply controls need per-review "am I editing right now" state that
   * outlives a single render pass — unlike the rest of this list, which is
   * fully rebuilt from API data on every load. So each review gets its own
   * mutable <article> plus a `rerender()` closure over local state, called
   * again on every reply-related interaction instead of reloading the list
   * from the network just to toggle a form open.
   */
  renderReview(review) {
    const articleEl = h('article', { class: 'product-reviews-app__review' });
    let isEditingReply = false;
    let replyError = null;

    const rerender = () => {
      // -- Left column: reviewer identity ----------------------------------
      // review.verified_purchase is computed fresh on every fetch, against
      // orders actually placed for this product (see the backend's
      // verified_buyers table) — not something submitted with the review.
      const authorChildren = [h('p', { class: 'product-reviews-app__review-name' }, [review.display_name])];
      if (review.verified_purchase) {
        authorChildren.push(
          h('span', { class: 'product-reviews-app__verified-badge' }, [
            h('span', { class: 'product-reviews-app__verified-badge-check', 'aria-hidden': 'true' }, ['✓']),
            ' Verified purchase',
          ]),
        );
      }
      // How many approved reviews this reviewer has written, site-wide —
      // see PublicReviewOut.reviewer_review_count.
      authorChildren.push(
        h('p', { class: 'product-reviews-app__review-count' }, ['Review ', h('strong', {}, [String(review.reviewer_review_count)])]),
      );
      const authorEl = h('div', { class: 'product-reviews-app__review-author' }, authorChildren);

      // -- Right column: rating, content, photos, reply --------------------
      const stars = h(
        'span',
        { class: 'product-reviews-app__stars', 'aria-hidden': 'true' },
        Array.from({ length: 5 }, (_, i) => buildStarIcon(i < review.rating)),
      );

      // Deliberately no pending/rejected status badge here — a customer's
      // own not-yet-approved review is included in this list (see
      // /reviews/public) so they can see the content came through, but its
      // moderation status isn't shown to them.
      const children = [stars];
      if (review.review_title) {
        children.push(h('p', { class: 'product-reviews-app__review-title' }, [`“${review.review_title}”`]));
      }
      const date = new Date(review.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      children.push(h('p', { class: 'product-reviews-app__review-date' }, [date]));
      if (review.review_text) {
        children.push(h('p', { class: 'product-reviews-app__review-text' }, [review.review_text]));
      }

      const photoUrls = review.photo_urls || [];
      if (photoUrls.length) {
        const gallery = h(
          'div',
          { class: 'product-reviews-app__photo-gallery' },
          photoUrls.map((url, index) => {
            const photoButton = h(
              'button',
              {
                type: 'button',
                class: 'product-reviews-app__photo-button',
                'aria-label': `View photo ${index + 1} of ${photoUrls.length} full size`,
              },
              [h('img', { class: 'product-reviews-app__photo', src: url, alt: '', loading: 'lazy' })],
            );
            photoButton.addEventListener('click', () => this.openLightbox(photoUrls, index));
            return photoButton;
          }),
        );
        children.push(gallery);
      }

      // Same for every visitor — the store's name (section setting) is
      // shown rather than the individual staff member's name (the API
      // doesn't send it here either — see PublicReviewOut). Edit/remove only
      // appear for a signed-in customerCanReply visitor, same as who can
      // post one in the first place.
      if (review.reply_text && !isEditingReply) {
        const replyChildren = [
          h('p', { class: 'product-reviews-app__reply-author' }, [this.storeName ? `Reply from ${this.storeName}` : 'Store reply']),
          h('p', { class: 'product-reviews-app__reply-text' }, [review.reply_text]),
        ];
        if (this.customerCanReply) {
          const editButton = h('button', { type: 'button', class: 'product-reviews-app__reply-action' }, ['Edit reply']);
          const removeButton = h(
            'button',
            { type: 'button', class: 'product-reviews-app__reply-action product-reviews-app__reply-action--danger' },
            ['Remove reply'],
          );
          editButton.addEventListener('click', () => {
            isEditingReply = true;
            replyError = null;
            rerender();
          });
          removeButton.addEventListener('click', async () => {
            const confirmed = await this.confirmAction('Remove this reply? This can’t be undone.');
            if (!confirmed) return;
            this.handleRemovePublicReply(review, () => rerender());
          });
          replyChildren.push(h('div', { class: 'product-reviews-app__reply-actions' }, [editButton, removeButton]));
        }
        children.push(h('div', { class: 'product-reviews-app__reply' }, replyChildren));
      }

      if (!review.reply_text && this.customerCanReply && !isEditingReply) {
        const replyTrigger = h('button', { type: 'button', class: 'product-reviews-app__reply-trigger' }, ['Reply']);
        replyTrigger.addEventListener('click', () => {
          isEditingReply = true;
          replyError = null;
          rerender();
        });
        children.push(replyTrigger);
      }

      if (isEditingReply) {
        const textarea = h('textarea', { rows: '3', placeholder: 'Write a reply…' });
        textarea.value = review.reply_text || '';

        const saveButton = h('button', { type: 'button', class: 'product-reviews-app__button' }, ['Save reply']);
        const cancelButton = h('button', { type: 'button', class: 'product-reviews-app__button product-reviews-app__button--secondary' }, ['Cancel']);
        saveButton.addEventListener('click', async () => {
          const text = textarea.value.trim();
          if (!text) {
            replyError = 'Reply cannot be empty.';
            rerender();
            return;
          }
          saveButton.disabled = true;
          try {
            const updated = await this.savePublicReply(review.id, text);
            review.reply_text = updated.reply_text;
            review.replied_at = updated.replied_at;
            isEditingReply = false;
            replyError = null;
          } catch (error) {
            replyError = error instanceof Error ? error.message : 'Could not save reply.';
          } finally {
            saveButton.disabled = false;
            rerender();
          }
        });
        cancelButton.addEventListener('click', () => {
          isEditingReply = false;
          replyError = null;
          rerender();
        });

        const formChildren = [textarea];
        if (replyError) {
          formChildren.push(h('p', { class: 'product-reviews-app__form-message product-reviews-app__form-message--error' }, [replyError]));
        }
        formChildren.push(h('div', { class: 'product-reviews-app__reply-form-actions' }, [saveButton, cancelButton]));
        children.push(h('div', { class: 'product-reviews-app__reply-form' }, formChildren));
      }

      const contentEl = h('div', { class: 'product-reviews-app__review-content' }, children);
      articleEl.replaceChildren(authorEl, contentEl);
    };

    rerender();
    return articleEl;
  }

  /** Shared by both the "Save reply" and future edit flows above. */
  async savePublicReply(reviewId, replyText) {
    const response = await fetch(`${this.apiBase}/reviews/${reviewId}/public-reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.storeKeyHeaders() },
      body: JSON.stringify({ customer_email: this.customerEmail, customer_name: this.customerName || null, reply_text: replyText }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(typeof body.detail === 'string' ? body.detail : 'Could not save reply. Please try again.');
    }
    return body;
  }

  async handleRemovePublicReply(review, onDone) {
    try {
      const url = new URL(`${this.apiBase}/reviews/${review.id}/public-reply`);
      url.searchParams.set('customer_email', this.customerEmail);
      const response = await fetch(url.toString(), { method: 'DELETE', headers: this.storeKeyHeaders() });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Could not remove reply.');
      review.reply_text = body.reply_text;
      review.replied_at = body.replied_at;
    } catch {
      // Rare (permission was already checked client-side to show the
      // button at all) — silently leave the reply as-is rather than a
      // disruptive alert; the button is still right there to retry.
    } finally {
      onDone();
    }
  }

  renderPagination(page, totalPages) {
    if (totalPages <= 1) {
      this.paginationEl.hidden = true;
      return;
    }
    this.paginationEl.hidden = false;
    this.setButtonDisabled(this.prevButton, page <= 1);
    this.setButtonDisabled(this.nextButton, page >= totalPages);
  }

  /**
   * Sets both the native `disabled` property (actually blocks the click)
   * and `aria-disabled` (what .product-reviews-app__page-button's CSS keys
   * its dimmed/not-allowed styling off of). Without the
   * latter, a disabled Next/Prev button is inert but looks identical to an
   * active one, which reads as "clicking it does nothing" rather than "this
   * is disabled".
   */
  setButtonDisabled(button, isDisabled) {
    button.disabled = isDisabled;
    button.setAttribute('aria-disabled', String(isDisabled));
  }
}

if (!customElements.get('product-reviews-app')) {
  customElements.define('product-reviews-app', ProductReviewsApp);
}
